import { useEffect } from "react";
import { z } from "zod";
import { sessionStore } from "../lib/store";
import { gaEventAppForegrounded } from "../tools/analytics/domains/appLifecycle.analytics";

/**
 * The `app_foregrounded` bridge message. The iOS and Android shells send it
 * through `window.__onNativeAppForegrounded` once the app is on screen: after a
 * launch (`cold_start`) or after a return from the background (`resume`).
 * `deep_link` is the link that opened the app (a universal or app link, a
 * custom scheme, a push), or null.
 *
 * A shell older than this message never calls the handler. Nothing is
 * reported for it, and nothing else changes.
 */
const appForegroundedSchema = z.object({
  source: z.enum(["cold_start", "resume"]),
  // z.url(), not z.string(): the analytics helper builds a `new URL` from it.
  // An unusable link is a malformed message and fails here, at the boundary,
  // not inside the shell's effect.
  deep_link: z.url().nullable(),
});

type AppForegrounded = z.infer<typeof appForegroundedSchema>;

/**
 * At most one event in this window per app session. A reader who checks the
 * app ten times an hour is one return, not ten. A return through a link is
 * always reported: which link brought the reader back is the question the
 * event answers.
 */
export const APP_FOREGROUNDED_THROTTLE_MS = 30 * 60 * 1000;

/**
 * When the last event was reported. Session storage, because a deep link loads
 * a new document in the same WebView and the window must survive it. A new
 * WebView (a new app process) starts a new app session with an empty store.
 */
const lastReportedAtStore = sessionStore(
  "app-foregrounded-last-reported-at",
  null,
  z.number().nullable()
);

export function isAppForegroundedReported(
  message: AppForegrounded,
  lastReportedAt: number | null,
  now: number
): boolean {
  if (message.deep_link !== null) return true;
  if (lastReportedAt === null) return true;
  return now - lastReportedAt >= APP_FOREGROUNDED_THROTTLE_MS;
}

/**
 * Report the message unless the window drops it. The window is checked and
 * stamped here, when the event is actually sent: a message queued in a
 * document that never mounts the shell (`/v/:token`) reports nothing, so it
 * must not hold the window either.
 */
function report(message: AppForegrounded): void {
  const now = Date.now();
  if (!isAppForegroundedReported(message, lastReportedAtStore.get(), now)) {
    return;
  }
  lastReportedAtStore.set(now);
  gaEventAppForegrounded({
    source: message.source,
    deepLink: message.deep_link,
  });
}

/**
 * Messages that arrived before the app shell mounted. The shells send the
 * message when the page has loaded, which on a cold start can come before the
 * effect that initialises Mixpanel: reported then, the event would reach GA4
 * and never Mixpanel.
 */
const queued: AppForegrounded[] = [];
let shellMounted = false;

function receiveAppForegrounded(payload: unknown): void {
  const message = appForegroundedSchema.parse(payload);
  if (!shellMounted) {
    queued.push(message);
    return;
  }
  report(message);
}

// Installed at module load, in the entry chunk, so the handler exists by the
// time the shells call it: they wait for the page load, and the entry chunk
// has run by then.
window.__onNativeAppForegrounded = receiveAppForegrounded;

/**
 * Report the `app_foregrounded` messages of this session. Call it in the app
 * shell AFTER `useMixpanel`: effects run in declaration order, so the queued
 * cold start is mirrored to an initialised Mixpanel.
 */
export function useAppForegroundedTracking(): void {
  useEffect(() => {
    shellMounted = true;
    for (const message of queued.splice(0)) report(message);
    return () => {
      shellMounted = false;
    };
  }, []);
}
